import React, { useState } from "react";
import { useLazySearchReminderTasksQuery, usePatchReminderTaskMutation } from "../services/reminders";
import { useClientContext } from "./ClientContext";
import { useFilterContext } from "./FilterContext";
import { patchReplace } from "../helpers/patchDoc";

const useClientEvents = (initSort = "deadlineDate+DESC", initLimit = 50) => {

    const [patchEventTrigger, patchResult] = usePatchReminderTaskMutation();

    const { id: masterAccountId } = useClientContext();

    const { filter, isReady } = useFilterContext();

    const [cacheKey, setCacheKey] = React.useState(new Date().valueOf());

    const [sort, setSort] = React.useState(initSort);
    const [limit, setLimit] = React.useState(initLimit);

    const [load, { data, isLoading, isFetching, isError, error, isUninitialized }] = useLazySearchReminderTasksQuery();

    const { pagination, results } = data || { pagination: { page: 1, pageSize: limit, totalCount: 0, totalPages: 1 }, results: [] };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isNextPageLoading = isFetching === true || isLoading === true;

    const clear = (newSort) => {
            // safety check to make sure we don't reload when already loading!
            if (isNextPageLoading || !isReady) {
            return;
        }

        const newCacheKey = new Date().valueOf();

        load({
            ...filter,
            masterAccountId: masterAccountId,
            cacheKey: newCacheKey,
            sort,
            limit,
            page: 1,
        }).unwrap().then(_ => {
            setCacheKey(_ => newCacheKey)
            if (newSort) {
                setSort(_ => newSort);
            }
        });
    };

    const reload = (e) => {
        if (e && typeof e.preventDefault === "function") {
            e.preventDefault();
        }

        clear();
    };

    const isRowLoaded = (index) => !hasMore || index < results.length;

    const loadMore = () => {
        // safety check to make sure we don't reload when already loading!
        if (isNextPageLoading) {
            return;
        }

        load({
            cacheKey,
            sort,
            limit,
            page: page + 1,
            ...filter,
            masterAccountId: masterAccountId,
        });
    };

    const sortTable = (property) => {
        // we want the sort to initially be descending ??
        let newDirection = "";
        let [sortProperty, direction] = sort.split("+");

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
    };

    const patchEvent = (reminderId, property, value) => {
        return new Promise((resolve, reject) => {
            patchEventTrigger({ reminderId, operations: [patchReplace(property, value)] })
                .unwrap()
                .then(resolve, reject);
        });
    }

    React.useEffect(clear, [filter, sort, isReady]);

    return {
        results,
        sort,
        totalCount,
        isError,
        error,
        isNextPageLoading,
        clear,
        isRowLoaded,
        loadMore,
        sortTable,
        reload,
        patchEvent,
        patchResult
    };
}

export default useClientEvents;